/**
 * skyDrawSource — the source text of the injected `skyDraw.js` and its filename.
 * The DRAWING & CONSTELLATION-PROCESSING half of the former `skyHelpers.js` (its math/transforms
 * half is now `skyTransformsSource.js`). Kept as a string because it ships *inside the student's
 * sketch* (it can't import) and is eval'd verbatim by the AR viewer. Its behaviour is unit-tested
 * by evaluating this string (skyDrawSource.test.js), so there is one source of truth.
 *
 * Draws the fixed celestial sphere: bakes each star's RA/Dec into a dome position
 * (setConstellationsPositions), walks the catalogue (drawEachConstellation) with either the
 * built-in per-star drawing (drawDefaultStarFromConstellation / drawDefaultStarConnectingLines)
 * or the sketch's own, bakes the result into reusable geometry (buildConstellations*Geometry),
 * and wraps a texture around the whole sky (drawCelestialDome) — plus the flattening that accepts
 * either a flat {star:{ra,dec}} map or a nested {constellation:{star}} one.
 * Call these AFTER lockStarsToSky() (from skyTransforms.js) has oriented the frame.
 *
 * One helper here is NOT celestial: resetCamera() swaps the AR/device camera for a plain fixed one
 * that reproduces p5's 2D pixel coordinates, so a sketch can draw a HUD or screen-space labels
 * (typically at positions from worldToScreenDepth(), in skyTransforms.js) over the AR view.
 *
 * STANDALONE: it carries its own private raDecToVec so it works whether or not skyTransforms.js is
 * present — it never reads or clobbers window.raDecToVec. p5 is reached via window.__stemartsP5
 * (the AR bridge's shared instance) or window (the editor's global-mode p5), so the one file works
 * in both.
 */

export const SKY_DRAW_FILENAME = 'skyDraw.js';

export const SKY_DRAW_SOURCE = `// skyDraw.js — celestial-sphere drawing & constellation processing. Generated by the Sketch menu.
// Frame: X=East, Y=DOWN, Z=South (North=-Z, up=-Y). RA/Dec in DEGREES (J2000).
// Call after lockStarsToSky().
(function () {
  var D2R = Math.PI / 180;
  var DOME_R = 2000;// Celestial Dome Radius
  
  function p5ctx() { return window.__stemartsP5 || window; }
//----------------------------------------------------------------------------------------------------
  // Fixed point on the celestial sphere for RA/Dec (degrees): equatorial unit p5.Vector.
  // Kept private (not window.raDecToVec) so this file is standalone — identical to skyTransforms.js.
  function raDecToVec(raDeg, decDeg) {
    var a = raDeg * D2R, d = decDeg * D2R, cd = Math.cos(d);
    return p5ctx().createVector(cd * Math.cos(a), cd * Math.sin(a), Math.sin(d));
  }
  
//----------------------------------------------------------------------------------------------------
  // Accept either a flat {name:{ra,dec}} map or a nested {constellation:{name:{ra,dec}}} one, and
  // return a flat array of star objects.
  window.flattenStars = function (stars) {
    var out = [];
    if (!stars) return out;
    var keys = Object.keys(stars);
    var flat = keys.length && typeof stars[keys[0]].ra === 'number';
    keys.forEach(function (k) {
      if (flat) out.push(stars[k]);
      else Object.keys(stars[k]).forEach(function (s) { out.push(stars[k][s]); });
    });
    return out;
  }

//----------------------------------------------------------------------------------------------------
  window.drawDefaultStarFromConstellation = function(constellation, starName){
    const star = constellation[starName];
    var p = p5ctx();
    p.push();
    p.translate(star.pos.x, star.pos.y, star.pos.z);
    // we use boxes as it is "cheaper" than spheres. But you can draw any shape here.
    p.box(10);
    p.pop();
  }

//----------------------------------------------------------------------------------------------------
  window.drawDefaultStarConnectingLines = function(constellation, starName){
    const star = constellation[starName];
    if (!star.connectsTo) return;
    var p = p5ctx();
    for (const other of star.connectsTo) {
      if (starName < other && constellation[other]) {
        const o = constellation[other];
        p.line(star.pos.x, star.pos.y, star.pos.z, 
             o.pos.x, o.pos.y, o.pos.z);
      }
    }
  }

//----------------------------------------------------------------------------------------------------  
  window.setStarsNames = function(){
    if(!window.constellations) return;
    Object.keys(window.constellations).forEach((constellationName)=>{
      Object.keys(constellations[constellationName]).forEach((starName)=>{
        let star = window.constellations[constellationName][starName];
        star.name = starName;
      });
    }); 
  }


//----------------------------------------------------------------------------------------------------  
  window.setConstellationsPositions = function(){
    if(!window.constellations) return;
    
    Object.keys(window.constellations).forEach((constellationName)=>{
      Object.keys(constellations[constellationName]).forEach((starName)=>{
        let star = window.constellations[constellationName][starName];
        star.pos = raDecToVec(star.ra, star.dec).mult(DOME_R);
      });
    }); 
  }

//----------------------------------------------------------------------------------------------------
  window.drawEachConstellation = function(drawFunc, constellationKeys) {
    const constellations = window.constellations || {};
    
    // Normalize constellationKeys into an array of keys to use
    let keys;
    if (constellationKeys == null) {
      keys = Object.keys(constellations);
    } else if (typeof constellationKeys === 'string') {
      keys = [constellationKeys];
    } else if (Array.isArray(constellationKeys)) {
      keys = constellationKeys;
    } else {
      throw new TypeError('Expected a string, an array of strings, or null/undefined');
    }
  
    for (const key of keys) {
      let constellation = constellations[key];
      if (!constellation) {
        console.warn('Constellation ', key , ' not found');
        continue;
      }
      
      for (const [name, star] of Object.entries(constellation)) {
        if (!star) {
          console.warn('star ' , name, ' not found');
          return;
        }
        drawFunc(constellation, name);
      }
    }
  }
  
//----------------------------------------------------------------------------------------------------
  window.buildConstellationsGeometry= function (drawFunc, constellationKeys) {
    if(typeof drawFunc !== 'function'){
      console.error("buildConstellationsGeometry argument is not a function");
      return null;
    }
    window.setConstellationsPositions();
    var p = p5ctx();
    return p.buildGeometry(()=>{
      drawEachConstellation(drawFunc, constellationKeys);
    });
  }
  
//----------------------------------------------------------------------------------------------------
  window.buildConstellationsDefaultGeometry= function ( constellationKeys) {
    return window.buildConstellationsGeometry(window.drawDefaultStarFromConstellation, constellationKeys);
  }

//----------------------------------------------------------------------------------------------------
  window.buildConstellationsLinesDefaultGeometry= function ( constellationKeys) {
    return window.buildConstellationsGeometry(window.drawDefaultStarConnectingLines, constellationKeys);
  }
//----------------------------------------------------------------------------------------------------
  window.drawCelestialDome= function ( domeTexure) {
    var p = p5ctx();
    p.push();
    p.rotateX(- Math.PI /2);//-90
    p.rotateY(- Math.PI /2);//-90
  
    p.texture(domeTexure);
    p.noStroke();
    p.sphere(DOME_R *2);
    p.pop();
  }

//----------------------------------------------------------------------------------------------------
  // Replace the AR (device) camera with a plain fixed one that reproduces p5's 2D pixel
  // coordinates, so a sketch can draw a HUD / screen-space labels over the AR view. Pairs with
  // worldToScreenDepth() (skyTransforms.js), which reports pixels in exactly this system.
  //
  // WHY (width/2, height/2, 800) IS EXACTLY 2D MODE, not a magic number: p5's own default camera
  // sits at z = 800 (Camera._computeCameraDefaultSettings: defaultEyeZ = 800) and DERIVES its
  // field of view from it, 2*atan(height/2/800) — the FOV for which the plane z = 0 is exactly
  // one world unit per pixel. createCamera() gives us that default; setPosition then slides the
  // eye (keeping the -Z look direction, since it moves the center by the same delta) so the axis
  // passes through (width/2, height/2) instead of the origin. Net effect at z = 0: world (0,0)
  // is the TOP-LEFT of the canvas, (width, height) the bottom-right, +X right, +Y down, 1 unit =
  // 1 pixel. It also restores p5's default projection, so the device FOV is dropped too.
  //
  // Cached on window because p5 2.x's createCamera() does NOT make the new camera current (only
  // setCamera does, hence the explicit call), and rebuilding one per frame is wasteful.
  //
  // SCOPE — this does not leak, and cannot corrupt the AR view:
  //   * setCamera writes renderer state through states.setValue, which push()/pop() restore. The
  //     bridge runs the sketch's draw inside its own push()/pop(), so the AR camera is back by
  //     the next frame — call this every frame you want it.
  //   * the bridge's per-frame p.camera()/p.perspective() CLONE the current camera before
  //     mutating it (Renderer3D.prototype.camera), so the device pose is never written into
  //     window.__stemartsNoCamera.
  // Caveat: the cached camera's FOV/aspect are computed from the canvas size at creation, so
  // after a canvas resize the 1:1 pixel mapping drifts until the camera is rebuilt.
  window.resetCamera = function(){
    var p = p5ctx();

    if(!window.__stemartsNoCamera){
      window.__stemartsNoCamera = p.createCamera();
    }

    window.__stemartsNoCamera.setPosition(p.width/2, p.height/2, 800);

    p.setCamera(window.__stemartsNoCamera);

  }

})();
`;
